/**
 * SettingsHubPage - Unified Settings Navigation Hub
 * 
 * Central hub for all user settings with navigation cards to sub-pages.
 * Replaces the old flat SettingsPage and ProfileEditPage pattern.
 * 
 * Related Issues: #596, #597, #598 (Settings/Edit page consolidation)
 * Architecture: Follows iOS/Android settings pattern with categorized sub-pages
 */

import { useNavigate } from 'react-router-dom';
import { TopBar } from '../components';
import { 
  User, 
  Bell, 
  Accessibility,
  ChevronRight
} from 'lucide-react';

interface SettingsCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  path: string;
}

function SettingsCard({ icon, title, description, path }: SettingsCardProps) {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(path)}
      className="cursor-pointer hover:bg-oro-surface-hover transition-colors bg-oro-surface border border-oro-border rounded-xl"
    >
      <div className="flex items-center gap-4 p-4">
        <div className="shrink-0 w-10 h-10 bg-oro-surface-alt rounded-full flex items-center justify-center">
          {icon}
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-text-primary">{title}</h3>
          <p className="text-sm text-text-secondary truncate">{description}</p>
        </div>
        
        <ChevronRight className="shrink-0 w-5 h-5 text-text-muted" />
      </div>
    </div>
  );
}

export default function SettingsHubPage() {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar 
        title="Settings" 
        onBack={() => navigate('/me')}
      />
      
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto p-4 space-y-4">
          
          {/* Profile Settings */}
          <SettingsCard
            icon={<User className="w-5 h-5 text-accent" />}
            title="Profile"
            description="Manage your personal information, photo, and sports"
            path="/settings/profile"
          />

          {/* Notifications */}
          <SettingsCard
            icon={<Bell className="w-5 h-5 text-accent" />}
            title="Notifications"
            description="Email notifications and event reminders"
            path="/settings/notifications"
          />

          {/* Accessibility */}
          <SettingsCard
            icon={<Accessibility className="w-5 h-5 text-accent" />}
            title="Accessibility"
            description="Reduce motion and adjust display preferences"
            path="/settings/accessibility"
          />

          {/* Privacy & Account deferred to v1.0.0 - no fake toggles */}
          {/* Privacy: Blocked by backend (no public_profile enforcement) */}
          {/* Account: Blocked by GDPR endpoints (no export/delete) */}

        </div>
      </div>
    </div>
  );
}
